import React, { useState, useEffect } from 'react';
import { TextField, Button, Paper, Typography, MenuItem, Select, InputLabel, FormControl, Box, CircularProgress, Switch, FormControlLabel } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { createDepartment, updateDepartment } from '../../redux/actions/department/departmentAction';
import { toast } from 'react-toastify';

const DepartmentForm = ({ selectedDepartment, onReset }) => {
  const dispatch = useDispatch();
  const { createLoading, updateLoading } = useSelector((state) => state.department);
  
  const [formData, setFormData] = useState({
    name: '',
    status: 'Active',
    manual_remark:'',
    inactive_remark_category:'',
    process_order_no: 0,
    enable_stone_cols: 0,
    enable_pearl_cols: 0,
    is_end_dept: 0
  });

  useEffect(() => {
    if (selectedDepartment) {
      setFormData(selectedDepartment);
    } else {
      setFormData({ name: '', status: 'Active', manual_remark:'', inactive_remark_category:'', process_order_no: 0, enable_stone_cols: 0, enable_pearl_cols: 0, is_end_dept: 0 });
    }
  }, [selectedDepartment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedDepartment) {
        await dispatch(updateDepartment(selectedDepartment.id, formData));
        toast.success('Department updated successfully');
      } else {
        await dispatch(createDepartment(formData));
        toast.success('Department created successfully');
      }
      setFormData({ name: '', status: 'Active', manual_remark: '', inactive_remark_category: '', process_order_no: 0, enable_stone_cols: 0, enable_pearl_cols: 0, is_end_dept: 0 });
      if (onReset) onReset();
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || 'An error occurred while processing department';
      toast.error(errorMessage);
    }
  };

  const handleClear = () => {
    setFormData({ name: '', status: 'Active', manual_remark: '', inactive_remark_category: '', process_order_no: 0, enable_stone_cols: 0, enable_pearl_cols: 0, is_end_dept: 0 });
    if (onReset) onReset();
  };

  const isLoading = createLoading || updateLoading;

  return (
    <Paper elevation={3} sx={{ p: 3, boxShadow: 'none', background: 'transparent'  }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }} gutterBottom>
        {selectedDepartment ? 'Edit Department' : 'Add Department'}
      </Typography>
      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          margin="normal"
          required
          autoComplete="off"
          disabled={isLoading}
        />
        <FormControl fullWidth margin="normal" required>
          <InputLabel>Status</InputLabel>
          <Select
            name="status"
            value={formData.status}
            label="Status"
            onChange={handleChange}
            disabled={isLoading}
          >
            <MenuItem value="Active">Active</MenuItem>
            <MenuItem value="Inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
        <TextField
          fullWidth
          label="Process Order No"
          name="process_order_no"
          type="number"
          value={formData.process_order_no}
          onChange={handleChange}
          margin="normal"
          inputProps={{ min: 0 }}
          autoComplete="off"
          disabled={isLoading}
        />
        <TextField
          fullWidth
          label="Manual Remark"
          name="manual_remark"
          value={formData.manual_remark}
          onChange={handleChange}
          margin="normal"
          autoComplete="off"
          disabled={isLoading}
        />
        <TextField
          fullWidth
          label="Inactive Remark"
          name="inactive_remark_category"
          value={formData.inactive_remark_category}
          onChange={handleChange}
          margin="normal"
          autoComplete="off"
          disabled={isLoading}
        />
        <FormControlLabel
          control={
            <Switch
              checked={!!formData.enable_stone_cols}
              onChange={e => setFormData(prev => ({ ...prev, enable_stone_cols: e.target.checked ? 1 : 0 }))}
              name="enable_stone_cols"
              color="primary"
              disabled={isLoading}
            />
          }
          label="Enable Stone Columns"
          sx={{ mt: 2 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={!!formData.enable_pearl_cols}
              onChange={e => setFormData(prev => ({ ...prev, enable_pearl_cols: e.target.checked ? 1 : 0 }))}
              name="enable_pearl_cols"
              color="primary"
              disabled={isLoading}
            />
          }
          label="Enable Pearl Columns"
          sx={{ mt: 2 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={!!formData.is_end_dept}
              onChange={e => setFormData(prev => ({ ...prev, is_end_dept: e.target.checked ? 1 : 0 }))}
              name="is_end_dept"
              color="primary"
              disabled={isLoading}
            />
          }
          label="Is End Department"
          sx={{ mt: 2 }}
        />
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            fullWidth
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isLoading ? 'Processing...' : (selectedDepartment ? 'Update' : 'Add')}
          </Button>
          <Button 
            type="button" 
            variant="outlined" 
            color="primary" 
            fullWidth
            onClick={handleClear}
            disabled={isLoading}
          >
            Clear
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default DepartmentForm;
